/* eslint-disable import/prefer-default-export */
import axios, { AxiosError } from 'axios';
import { FILTER_API_HOST } from 'Config/constants';
import { getTimestampAndISOTime } from 'helpers/datetime';
import showToast from 'helpers/showToast';
import { isNotProduction } from 'lib/environment';
import { getIdToken } from 'lib/firebase/auth';
import {
  createDocRef,
  firestoreRunTransaction,
  getDocument
} from 'lib/firebase/utils';
import { DateAndTime } from 'services/interface';
import { GuestDetails, ShopProductSummary } from 'services/shops/interface';

export type WorkshopProduct = ShopProductSummary;

interface GetWorkShopDetailsOption {
  productId: string;
  publisherId: string;
}

export const getWorkshopDetails = async (option: GetWorkShopDetailsOption) => {
  const { productId, publisherId } = option ?? {};

  if (!publisherId || !productId) {
    return null;
  }

  const response = await axios.get<WorkshopProduct>(
    `${FILTER_API_HOST}/api/v1/publishers/${publisherId}/shops/${productId}`
  );

  return response?.data;
};

const marketplaceCheckoutAPI = `${process.env.REACT_APP_MARKETPLACE_API_HOST}/api/v1/checkout/sessions`;

export interface CheckoutSessionConfig {
  action: 'CREATE' | 'COMPLETE' | 'CREATE_RETREAT';
  owner_id: string;
  shop_product_id: string;
  session_id?: string;
  accommodation_option_id?: string;
}

interface NewCheckoutSessionAPIResponse {
  id: string;
  object: string;
  amount_subtotal: number;
  amount_total: number;
  automatic_tax?: {
    enabled: boolean;
  };
  billing_address_collection?: string;
  cancel_url: string;
  client_reference_id?: string;
  consent?: string;
  consent_collection?: string;
  currency: string;
  customer: string;
  customer_details: {
    email: string;
    phone?: string;
    tax_exempt: string;
    tax_ids?: string[];
  };
  customer_email?: string;
  expires_at: number;
  livemode: boolean;
  locale?: string;
  metadata: {
    user_id: string;
    owner_id: string;
    event_id: string;
    shop_product_id: string;
    event_type: string;
  };
  mode: string;
  payment_intent: string;
  // payment_method_options: {};
  payment_method_types: string[];
  payment_status: string;
  phone_number_collection?: {
    enabled: boolean;
  };
  status: string;
  success_url: string;
  total_details: {
    amount_discount: number;
    amount_shipping: number;
    amount_tax: number;
  };
  url: string;
}

interface FetchNewCheckoutSessionIdApiConfig {
  headers: {
    Authorization: string;
    'Use-Stripe-Test-Environment'?: boolean;
  };
}

export const fetchNewCheckoutSessionId = async (
  data: CheckoutSessionConfig
) => {
  const token = await getIdToken();
  const config: FetchNewCheckoutSessionIdApiConfig = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  if (isNotProduction) {
    config.headers['Use-Stripe-Test-Environment'] = true;
  }

  const requestData = {
    success_cancel_page: 'WEB',
    ...data
  };

  const response = await axios
    .post<NewCheckoutSessionAPIResponse>(marketplaceCheckoutAPI, requestData, {
      ...config,
      timeout: 30000
    })
    .catch((e: AxiosError) => {
      showToast({
        toastContent: e?.response?.data?.message ?? e?.message
      });
      return Promise.reject(e);
    });

  return response?.data;
};

export const registerUserForFreeShopProduct = async (
  data: CheckoutSessionConfig
) => {
  const token = await getIdToken();
  const config: FetchNewCheckoutSessionIdApiConfig = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  if (isNotProduction) {
    config.headers['Use-Stripe-Test-Environment'] = true;
  }

  const response = await axios
    .post(marketplaceCheckoutAPI, data, config)
    .catch((e: AxiosError) => {
      showToast({
        toastContent: e?.response?.data?.message ?? e?.message
      });
      return Promise.reject(e);
    });

  return response?.data;
};

export interface UserAndWorkshopRelationData {
  id: string;
  is_bookmarked: boolean;
  is_purchased: boolean;
  purchased_at: DateAndTime;
  bookmarked_at: DateAndTime;
  created_at: DateAndTime;
  updated_at: DateAndTime;
  synced_at: string;
  shop_product_summary: {
    id: string;
    owner_id: string;
    status: 'APPROVED' | 'PENDING';
    type: string;
  };
  guest_details?: GuestDetails[];
  accommodation_option_id_purchased?: string;
}

interface GetUserAndWorkshopRelationArgument {
  userId: string;
  workshopId: string;
}

export const getUserAndWorkshopRelation = async ({
  userId,
  workshopId
}: GetUserAndWorkshopRelationArgument) => {
  const docRef = await createDocRef(
    `/users/${userId}/shop_product_relation/${workshopId}`
  );
  return getDocument(docRef).then(ref => {
    if (!ref.exists) {
      return null;
    }

    const relationData: UserAndWorkshopRelationData = {
      ...ref.data()
    } as UserAndWorkshopRelationData;

    return relationData;
  });
};

interface CompleteCheckoutSessionHeaderConfig {
  headers: {
    Authorization: string;
    'Use-Stripe-Test-Environment'?: boolean;
  };
}

interface CompleteShopProductCheckoutSessionParams {
  requestDetails: {
    action: 'COMPLETE';
    owner_id: string;
    shop_product_id: string;
    session_id: string;
  };
  token: string;
}

export const completeShopProductCheckoutSession = async ({
  requestDetails,
  token
}: CompleteShopProductCheckoutSessionParams) => {
  const config: CompleteCheckoutSessionHeaderConfig = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  if (isNotProduction) {
    config.headers['Use-Stripe-Test-Environment'] = true;
  }

  const status = await axios
    .post(marketplaceCheckoutAPI, requestDetails, config)
    .then(() => {
      return 'CHECKOUT_SUCCESS';
    })
    .catch((e: AxiosError) => {
      showToast({
        toastContent: e?.response?.data?.message ?? e?.message
      });
      return Promise.reject(e);
    });

  return status;
};

export interface StoreAgreementDateParams {
  userId: string;
  eventId: string;
  eventSummary?: {
    id: string;
    type?: string;
    owner_id: string;
  };
}

interface UserWorkshopEventRelationUpdatedDetailsSchema {
  id?: string;
  agreement_accepted_at?: DateAndTime;
  created_at?: DateAndTime;
  updated_at?: DateAndTime;
  synced_at?: string;
  event_summary?: {
    id: string;
    type?: string;
    owner_id: string;
  };
}

export const storeWorkshopAgreementDate = async ({
  userId,
  eventId,
  eventSummary
}: StoreAgreementDateParams) => {
  const userEventRelationDocRef = await createDocRef(
    `/users/${userId}/event_relation/${eventId}`
  );
  return firestoreRunTransaction(async transaction => {
    const userEventRelationDoc = await transaction.get(userEventRelationDocRef);
    const currentDate = getTimestampAndISOTime();
    let userWorkshopEventRelationUpdatedDetails: UserWorkshopEventRelationUpdatedDetailsSchema =
      {
        updated_at: currentDate,
        synced_at: currentDate.iso_8601_datetime_tz
      };
    if (!userEventRelationDoc.exists) {
      userWorkshopEventRelationUpdatedDetails.agreement_accepted_at =
        currentDate;
      userWorkshopEventRelationUpdatedDetails.event_summary = eventSummary;
      userWorkshopEventRelationUpdatedDetails.created_at = currentDate;
    } else {
      const userEventRelationFireStoreData: UserWorkshopEventRelationUpdatedDetailsSchema =
        userEventRelationDoc.data() ?? {};
      const agreementDate =
        userEventRelationFireStoreData?.agreement_accepted_at ?? currentDate;

      userWorkshopEventRelationUpdatedDetails = {
        ...userEventRelationFireStoreData,
        ...userWorkshopEventRelationUpdatedDetails,
        agreement_accepted_at: agreementDate
      };
    }
    transaction.set(
      userEventRelationDocRef,
      userWorkshopEventRelationUpdatedDetails,
      { merge: true }
    );
  });
};
